import { getRequest, deleteRequest, postRequest, putRequest } from './rest'

export type FloorPayload = {
  id: string
  mappedinId: string
  name: string
  elevationIndex: number
  altitude: number
  orgId: string
}

type MatchedFloor = {
  mi: { id: string; name: string; elevation: number }
  rm: string
}

export async function saveFloorsToApi(orgId: string, matchedFloors: MatchedFloor[]) {
  for (const f of matchedFloors) {
    const payload = {
      orgId,
      name: f.rm,
      mappedinId: f.mi.id,
      elevationIndex: f.mi.elevation,
      altitude: f.mi.elevation * 4,
    }

    await postRequest({
      endpoint: '/api/v5/floors',
      data: payload,
      header: { org_id: orgId },
    })
  }
}

export async function deleteOldVenueData(orgId: string) {
  const headers = { org_id: orgId }

  const deleteEntities = async (endpoint: string) => {
    let page = 1
    const pageSize = 50
    const ids: string[] = []

    for (;;) {
      const res = await getRequest({
        endpoint: `/api/v5/${endpoint}?page=${page}&pageSize=${pageSize}`,
        header: headers,
      })
      const data = res.data.data || []
      ids.push(...data.map((item: { id: string }) => item.id))

      if (data.length < pageSize) break
      page++
    }

    for (const id of ids) {
      await deleteRequest({ endpoint: `/api/v5/${endpoint}/${id}`, header: headers })
    }
  }

  await deleteEntities('floors')
  await deleteEntities('spaces')
}

type Space = {
  id: string
  name: string
  floorId: string
  polygon: {
    coordinates: number[][][]
  }
}

export async function saveSpacesToApi(
  orgId: string,
  matchedFloors: MatchedFloor[],
  spaceMap: Record<string, Space[]>
) {
  const floorPages: FloorPayload[] = []
  let page = 1
  const pageSize = 50

  for (;;) {
    const res = await getRequest({
      endpoint: `/api/v5/floors?page=${page}&pageSize=${pageSize}`,
      header: { org_id: orgId },
    })

    const data = res.data.data || []
    floorPages.push(...data)
    if (data.length < pageSize) break
    page++
  }

  const floorIdMap = floorPages.reduce<Record<string, string>>((acc, floor) => {
    acc[floor.mappedinId] = floor.id
    return acc
  }, {})

  for (const floor of matchedFloors) {
    const spaces = spaceMap[floor.rm] || []
    const floorId = floorIdMap[floor.mi.id]

    if (!floorId) {
      console.warn(`❌ Skipping floor "${floor.rm}" — no saved floor ID found.`)
      continue
    }

    for (const space of spaces) {
      const payload = {
        floorId,
        mappedinId: space.id,
        name: space.name?.trim() || space.id,
        polygonCoordinates: space.polygon.coordinates,
      }

      await postRequest({
        endpoint: '/api/v5/spaces',
        data: payload,
        header: { org_id: orgId },
      })
    }
  }
}

type POIMatch = {
  poiId: string
  poiName: string
  coordinates: {
    lat: number
    long: number
    floorId: string
  }
  beaconId: string
  roomName: string
}

export async function savePOIsToApi(
  orgId: string,
  matchedPOIs: Record<string, POIMatch>,
  matchedFloors: MatchedFloor[],
  matchedSpaces: Record<string, Record<string, string>>
) {
  console.log('🛰️ Fetching existing spaces from API...')
  const allSpaces: { id: string; name: string }[] = []
  let page = 1
  const pageSize = 50

  for (;;) {
    const res = await getRequest({
      endpoint: `/api/v5/spaces?page=${page}&pageSize=${pageSize}`,
      header: { org_id: orgId },
    })

    const data = res.data.data || []
    allSpaces.push(...data)
    if (data.length < pageSize) break
    page++
  }

  console.log(`📦 Retrieved ${allSpaces.length} spaces from API`)
  const spaceLookup: Record<string, string> = {}
  for (const space of allSpaces) {
    spaceLookup[space.name] = space.id
  }
  console.log('📖 Space name → ID map:', spaceLookup)

  const floorIdMap = matchedFloors.reduce<Record<string, string>>((acc, f) => {
    acc[f.mi.id] = `${orgId}-${f.rm}`
    return acc
  }, {})

  for (const [poiKey, poi] of Object.entries(matchedPOIs)) {
    const floor = matchedFloors.find(f => f.mi.id === poi.coordinates.floorId)
    const floorName = floor?.rm || 'Unknown Floor'
    const spaceName = poi.roomName
    let spaceId = spaceLookup[spaceName]

    console.log('➡️ Processing POI:', {
      key: poiKey,
      poiName: poi.poiName || '(Unnamed)',
      beaconId: poi.beaconId,
      roomName: spaceName,
      floorId: poi.coordinates.floorId,
      resolvedFloor: floorName,
    })

    if (!spaceId) {
      console.warn(`⚠️ No spaceId found for POI "${poi.poiName}" (room: "${spaceName}")`)
      console.log('🔍 Attempting fuzzy match...')
      const fuzzyMatch = Object.entries(spaceLookup).find(([name]) =>
        name.toLowerCase().includes(spaceName.toLowerCase())
      )
      if (fuzzyMatch) {
        spaceId = fuzzyMatch[1]
        console.log(`✅ Fuzzy matched "${spaceName}" to "${fuzzyMatch[0]}" → ${spaceId}`)
      } else {
        console.warn(`❌ Could not find any match for roomName: "${spaceName}"`)
        continue
      }
    }

    const payload = {
      coordinates: {
        latitude: poi.coordinates.lat,
        longitude: poi.coordinates.long,
        altitude: 1 // Savong just beacon height. To the right is for full altitude including floor height -> (floor?.mi.elevation ?? 1) * 4,
      },
      spaceId,
      versionCode: 0,
    }

    console.log(`📡 Sending PUT request to update beacon ${poi.beaconId} with payload:`, payload)

    try {
      await putRequest({
        endpoint: `/api/v5/beacons/${poi.beaconId}`,
        data: payload,
        header: { org_id: orgId },
      })
      console.log(`✅ Beacon ${poi.beaconId} updated successfully.`)
    } catch (err) {
      console.error(`❌ Failed to update beacon ${poi.beaconId}:`, err)
    }
  }

  console.log('🎯 POI save operation completed.')
}